// views/Tarefas/ProjetoSeletor.tsx
//
// Seletor do Projeto de uma Solicitação. Era um <select> nativo: nome cortado no gatilho,
// lista cinza do sistema operacional e nenhuma pista de em que pé cada projeto está. Aqui cada
// projeto vem com a cor e o selo da etapa (mesma fonte da tela de Projetos, etapas.ts), os
// encerrados descem para um grupo próprio e a busca aparece quando a lista cresce.
//
// O menu abre num portal com posição fixa — mesmo padrão do DatePicker — porque a coluna do
// modal rola (overflow-y-auto) e cortaria um menu `absolute` na borda.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import type { Projeto, ProjetoEtapa } from '../../types';
import { configDaEtapa } from '../../src/lib/projetos/etapas';

interface ProjetoSeletorProps {
  projetos: Projeto[];
  value: number | null | undefined;
  onChange: (projetoId: number | null) => void;
  /** Sem permissão de vincular: mostra o projeto como selo estático. */
  somenteLeitura?: boolean;
}

// Encerrado não some da lista (reabrir vínculo antigo acontece), mas não disputa espaço com
// o que está andando.
const ETAPAS_ENCERRADAS: ProjetoEtapa[] = ['concluido', 'cancelado'];

// Largo o bastante para nome + selo da etapa caberem numa linha na maioria dos projetos.
const LARGURA_MENU = 360;
const ALTURA_ESTIMADA_MENU = 360;
/** Abaixo disso a busca só atrapalha — dá para ver a lista inteira de uma vez. */
const MINIMO_PARA_BUSCA = 7;

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const porNome = (a: Projeto, b: Projeto) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });

const BolinhaEtapa: React.FC<{ etapa: ProjetoEtapa }> = ({ etapa }) => (
  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: configDaEtapa(etapa).cor }} />
);

const SeloEtapa: React.FC<{ etapa: ProjetoEtapa }> = ({ etapa }) => {
  const cfg = configDaEtapa(etapa);
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap shrink-0 ${cfg.badge}`}>
      {cfg.rotulo}
    </span>
  );
};

const ProjetoSeletor: React.FC<ProjetoSeletorProps> = ({ projetos, value, onChange, somenteLeitura }) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [destaque, setDestaque] = useState(0);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number; largura: number } | null>(null);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buscaRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);

  const atual = value != null ? projetos.find(p => p.id === value) ?? null : null;

  const { ativos, encerrados } = useMemo(() => {
    const q = norm(busca.trim());
    const filtrados = q ? projetos.filter(p => norm(p.nome).includes(q)) : projetos;
    return {
      ativos: filtrados.filter(p => !ETAPAS_ENCERRADAS.includes(p.etapa)).sort(porNome),
      encerrados: filtrados.filter(p => ETAPAS_ENCERRADAS.includes(p.etapa)).sort(porNome),
    };
  }, [projetos, busca]);

  // Ordem da navegação por teclado = ordem visual. `null` é "Remover do projeto", no rodapé.
  const navegaveis: Array<number | null> = useMemo(
    () => [...ativos.map(p => p.id), ...encerrados.map(p => p.id), ...(atual ? [null] : [])],
    [ativos, encerrados, atual],
  );

  // Fecha ao clicar fora — o gatilho fica de fora da checagem para o próprio clique alternar.
  useEffect(() => {
    if (!aberto) return;
    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (gatilhoRef.current?.contains(alvo) || menuRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', aoClicar);
    return () => document.removeEventListener('mousedown', aoClicar);
  }, [aberto]);

  // Posição: abre para baixo se couber (ou se houver mais espaço embaixo), e nunca estoura a
  // borda direita da tela. Acompanha a rolagem do modal enquanto aberto.
  useEffect(() => {
    if (!aberto) return;
    const posicionar = () => {
      const r = gatilhoRef.current?.getBoundingClientRect();
      if (!r) return;
      const largura = Math.min(LARGURA_MENU, window.innerWidth - 16);
      const espacoAbaixo = window.innerHeight - r.bottom;
      const paraBaixo = espacoAbaixo >= ALTURA_ESTIMADA_MENU || espacoAbaixo >= r.top;
      setCoords({
        top: paraBaixo ? r.bottom + 6 : undefined,
        bottom: paraBaixo ? undefined : window.innerHeight - r.top + 6,
        left: Math.max(8, Math.min(r.left, window.innerWidth - largura - 8)),
        largura,
      });
    };
    posicionar();
    window.addEventListener('scroll', posicionar, true);
    window.addEventListener('resize', posicionar);
    return () => {
      window.removeEventListener('scroll', posicionar, true);
      window.removeEventListener('resize', posicionar);
    };
  }, [aberto]);

  // Limpa a busca ao FECHAR (não ao abrir): assim, na reabertura, a lista já está inteira quando
  // o destaque é calculado. Ao abrir: destaca o projeto atual e põe o foco onde dá para digitar.
  useEffect(() => {
    if (!aberto) { setBusca(''); return; }
    const i = navegaveis.indexOf(value ?? null);
    setDestaque(i >= 0 ? i : 0);
    const t = setTimeout(() => (buscaRef.current ?? menuRef.current)?.focus(), 20);
    return () => clearTimeout(t);
  }, [aberto]);

  useEffect(() => {
    listaRef.current?.querySelector<HTMLElement>(`[data-i="${destaque}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [destaque]);

  const escolher = (id: number | null) => {
    if (id !== (value ?? null)) onChange(id);
    setAberto(false);
    gatilhoRef.current?.focus();
  };

  const aoTeclar = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setDestaque(d => Math.min(d + 1, navegaveis.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setDestaque(d => Math.max(d - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (destaque < navegaveis.length) escolher(navegaveis[destaque]); }
    else if (e.key === 'Escape') { e.preventDefault(); setAberto(false); gatilhoRef.current?.focus(); }
  };

  if (somenteLeitura) {
    return atual ? (
      <span title={atual.nome} className="inline-flex items-center gap-2 max-w-full text-sm font-semibold text-gray-800 dark:text-gray-100">
        <BolinhaEtapa etapa={atual.etapa} />
        <span className="truncate">{atual.nome}</span>
      </span>
    ) : (
      <span className="text-sm text-gray-400 dark:text-gray-500">Sem projeto</span>
    );
  }

  const opcao = (p: Projeto, i: number) => {
    const selecionado = p.id === value;
    return (
      <button
        key={p.id}
        type="button"
        role="option"
        aria-selected={selecionado}
        data-i={i}
        onMouseEnter={() => setDestaque(i)}
        onClick={() => escolher(p.id)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left rounded-lg transition-colors ${
          i === destaque ? 'bg-gray-100 dark:bg-gray-700/70' : ''
        }`}
      >
        <BolinhaEtapa etapa={p.etapa} />
        <span className={`flex-1 min-w-0 text-sm leading-snug line-clamp-2 ${
          selecionado ? 'font-semibold text-primary dark:text-primary-light' : 'text-gray-700 dark:text-gray-200'
        }`}>
          {p.nome}
        </span>
        <SeloEtapa etapa={p.etapa} />
        <Check size={14} className={`shrink-0 text-primary dark:text-primary-light ${selecionado ? '' : 'invisible'}`} />
      </button>
    );
  };

  const tituloGrupo = (texto: string) => (
    <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{texto}</p>
  );

  const nada = ativos.length === 0 && encerrados.length === 0;

  return (
    <>
      <button
        ref={gatilhoRef}
        type="button"
        onClick={() => setAberto(v => !v)}
        // O foco só desce para a busca 20 ms depois de abrir; até lá as teclas caem aqui. Com o
        // menu aberto, Enter escolhe o destacado (o preventDefault em aoTeclar segura o clique
        // nativo, que só fecharia); o espaço continua sendo o clique que abre/fecha.
        onKeyDown={e => {
          if (aberto && e.key !== ' ') aoTeclar(e);
          else if (!aberto && e.key === 'ArrowDown') { e.preventDefault(); setAberto(true); }
        }}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        title={atual ? `${atual.nome} — ${configDaEtapa(atual.etapa).rotulo}` : undefined}
        className={`group inline-flex items-center gap-2 max-w-full pl-2.5 pr-2 py-1 rounded-lg border text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
          atual
            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary/40 hover:bg-primary/5 font-semibold text-gray-800 dark:text-gray-100'
            : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:text-primary hover:border-primary/50'
        } ${aberto ? 'border-primary/50 ring-2 ring-primary/15' : ''}`}
      >
        {atual ? (
          <>
            <BolinhaEtapa etapa={atual.etapa} />
            <span className="truncate">{atual.nome}</span>
          </>
        ) : (
          <>
            <Plus size={13} className="shrink-0" />
            <span className="truncate font-medium">Vincular a um projeto</span>
          </>
        )}
        <ChevronDown size={14} className={`shrink-0 text-gray-400 group-hover:text-primary transition-transform duration-200 ${aberto ? 'rotate-180' : ''}`} />
      </button>

      {aberto && coords && createPortal(
        <div
          ref={menuRef}
          tabIndex={-1}
          onKeyDown={aoTeclar}
          style={{
            position: 'fixed',
            left: coords.left,
            width: coords.largura,
            ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }),
          }}
          className="z-[10050] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden outline-none animate-fade-in"
        >
          {projetos.length >= MINIMO_PARA_BUSCA && (
            <div className="relative border-b border-gray-100 dark:border-gray-700">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={buscaRef}
                value={busca}
                onChange={e => { setBusca(e.target.value); setDestaque(0); }}
                placeholder="Buscar projeto…"
                className="w-full pl-9 pr-3 py-2.5 text-sm bg-transparent text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
              />
            </div>
          )}

          <div ref={listaRef} role="listbox" className="max-h-80 overflow-y-auto p-1">
            {projetos.length === 0 ? (
              <p className="px-3 py-6 text-sm text-gray-400 text-center">Nenhum projeto cadastrado.</p>
            ) : nada ? (
              <p className="px-3 py-6 text-sm text-gray-400 text-center">Nenhum projeto com "{busca.trim()}".</p>
            ) : (
              <>
                {ativos.length > 0 && (
                  <>
                    {encerrados.length > 0 && tituloGrupo('Em andamento')}
                    {ativos.map((p, i) => opcao(p, i))}
                  </>
                )}
                {encerrados.length > 0 && (
                  <>
                    {tituloGrupo('Encerrados')}
                    {encerrados.map((p, i) => opcao(p, ativos.length + i))}
                  </>
                )}
              </>
            )}
          </div>

          {atual && (
            <div className="border-t border-gray-100 dark:border-gray-700 p-1">
              <button
                type="button"
                data-i={navegaveis.length - 1}
                onMouseEnter={() => setDestaque(navegaveis.length - 1)}
                onClick={() => escolher(null)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors ${
                  destaque === navegaveis.length - 1 ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' : ''
                }`}
              >
                <X size={14} className="shrink-0" />
                Remover do projeto
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default ProjetoSeletor;
